import classNames from "classnames";
import React, { useRef } from "react";
import { Key } from "ts-key-enum";

import { ProductOptionDropdownSelector } from "../../../common/ProductOptionDropdownSelector";
import { VariantOptionBlockInputTypes } from "../../../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValue,
} from "../../../models/catalogue";
import { getOptionValueHash } from "../../../utils/catalogue";
import { notEmpty, unique } from "../../../utils/functional";
import { sortProductOptions } from "../../../utils/sorting";
import { slugify } from "../../../utils/text";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { IRootState as IReduxState } from "../../reducers.interfaces";
import { setOptionValue } from "../reducers";
import { rootProductSelector } from "../selectors";
import { getOptionValueByIdx, getOptionValueSet } from "../utils";

const getOptionValues = (
    state: Pick<IReduxState, "configurator">,
    attributeCode: IOptionCode,
): IOptionSingleValue[] => {
    const rootProduct = rootProductSelector(state.configurator);
    if (!rootProduct) {
        return [];
    }
    const children = rootProduct.children;
    if (!children || children.length <= 0) {
        const attr = rootProduct.attributes[attributeCode];
        if (!attr) {
            return [];
        }
        return Array.isArray(attr.value)
            ? [...attr.value]
            : [attr.value as IOptionSingleValue];
    }
    const values = children
        .reduce<Array<IOptionSingleValue | null>>((memo, child) => {
            const attr = child.attributes[attributeCode];
            return memo.concat(attr ? attr.value : null);
        }, [])
        .filter(notEmpty)
        .filter(unique);
    return sortProductOptions(attributeCode, values);
};

const handleDropdownChange = (
    e: React.FormEvent<HTMLSelectElement | HTMLInputElement>,
    namespace: string | null,
    attributeCode: IOptionCode,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    if (!namespace) {
        return;
    }
    dispatch(
        setOptionValue({
            namespace,
            code: attributeCode,
            index: 0,
            totalNumValues: 1,
            value: e.currentTarget.value,
        }),
    );
};

const handleOptionClick = (
    event: React.MouseEvent<HTMLElement>,
    value: IOptionSingleValue,
    namespace: string | null,
    attributeCode: IOptionCode,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    event.preventDefault();
    if (!namespace) {
        return;
    }
    dispatch(
        setOptionValue({
            namespace,
            code: attributeCode,
            index: 0,
            totalNumValues: 1,
            value,
        }),
    );
};

const handleOptionKeyDown = (
    event: React.KeyboardEvent<Element>,
    index: number,
    namespace: string | null,
    attributeCode: IOptionCode,
    optionValues: IOptionSingleValue[],
    optionRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    if (!namespace) {
        return;
    }

    const prevIndex = index - 1;
    const nextIndex = index + 1;

    const prevValue = optionValues[prevIndex];
    const nextValue = optionValues[nextIndex];

    const prevRef = optionRefs.current[prevIndex];
    const nextRef = optionRefs.current[nextIndex];

    // Left arrow
    if (event.key === Key.ArrowLeft && prevValue && prevRef) {
        dispatch(
            setOptionValue({
                namespace,
                code: attributeCode,
                index: 0,
                totalNumValues: 1,
                value: prevValue,
            }),
        );
        prevRef.focus();
    }

    // Right arrow
    if (event.key === Key.ArrowRight && nextValue && nextRef) {
        dispatch(
            setOptionValue({
                namespace,
                code: attributeCode,
                index: 0,
                totalNumValues: 1,
                value: nextValue,
            }),
        );
        nextRef.focus();
    }
};

const buildOptionSelector = (
    index: number,
    attributeCode: IOptionCode,
    selectedValues: IOptionValue | undefined,
    optionValues: IOptionSingleValue[],
    namespace: string | null,
    optionRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>,
    dispatch: ReturnType<typeof useAppDispatch>,
): React.JSX.Element => {
    const selectedValueSet = getOptionValueSet(selectedValues);
    const value = optionValues[index];
    const isSelected = selectedValueSet.has(value);

    const classes = classNames({
        "pdp-change-variant-option-block__option": true,
        "pdp-change-variant-option-block__option--selected": isSelected,
    });

    // Build IDs for relationship to tab controls. These IDs must match the IDs used by PDPSelectedVariantConditionalBlock.
    const tabControlID = slugify(
        `change-variant-block-${attributeCode}-${value}`,
    );
    const tabPanelID = slugify(
        `variant-conditional-block-${attributeCode}-${value}`,
    );

    return (
        <button
            key={getOptionValueHash(value)}
            ref={(ref) => {
                optionRefs.current[index] = ref;
            }}
            id={tabControlID}
            role="tab"
            aria-selected={isSelected}
            aria-controls={tabPanelID}
            className={classes}
            onClick={(e) => {
                handleOptionClick(e, value, namespace, attributeCode, dispatch);
            }}
            onKeyDown={(e) => {
                handleOptionKeyDown(
                    e,
                    index,
                    namespace,
                    attributeCode,
                    optionValues,
                    optionRefs,
                    dispatch,
                );
            }}
            tabIndex={isSelected ? 0 : -1}
        >
            {value}
        </button>
    );
};

export const PDPChangeVariantOptionBlock = (props: {
    attributeCode: IOptionCode;
    inputType?: string;
}): React.ReactNode => {
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const dispatch = useAppDispatch();

    const optionValues = useAppSelector((state) =>
        getOptionValues(state, props.attributeCode),
    );
    const rootProduct = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );
    const namespace = rootProduct ? rootProduct.product_class_slug : null;
    const selectedValues = useAppSelector(
        (state) => state.configurator.ui.optionValues[props.attributeCode],
    );

    if (props.inputType === VariantOptionBlockInputTypes.DROPDOWN) {
        const htmlID = "productOption";
        const prefixName = props.attributeCode.substr(
            props.attributeCode.indexOf("_") + 1,
            props.attributeCode.length,
        );
        const prefixNameCapped =
            prefixName[0].toUpperCase() + prefixName.substr(1);

        return (
            <ProductOptionDropdownSelector
                selectorID={htmlID}
                code={props.attributeCode}
                value={getOptionValueByIdx(selectedValues, 0) || ""}
                options={optionValues}
                prefixNameCapped={prefixNameCapped}
                configuratorClass={"product-option-dropdown"}
                prefixClass={""}
                selectClass={""}
                onChange={(e) => {
                    handleDropdownChange(
                        e,
                        namespace,
                        props.attributeCode,
                        dispatch,
                    );
                }}
            />
        );
    }

    return (
        <nav role="tablist">
            {optionValues.map((_, index) => {
                return buildOptionSelector(
                    index,
                    props.attributeCode,
                    selectedValues,
                    optionValues,
                    namespace,
                    optionRefs,
                    dispatch,
                );
            })}
        </nav>
    );
};
